import { Tag } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { Link } from 'components/link';
import { FloatingTooltip } from 'components/floating-tooltip';
import classNames from 'classnames';
import { parseLocalDateTime } from 'utils/dates';
import type { EvaluationCalculation } from '../types';
import style from './evaluation-tile.module.scss';

interface EvaluationTileProps {
  calculation: EvaluationCalculation;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * Presentational tile for a single evaluation calculation in the left panel.
 * Displays the calculation ID, saved status, scenario link, and metadata.
 *
 * @param {EvaluationTileProps} props - Component props.
 * @return {JSX.Element} The evaluation tile.
 */
export const EvaluationTile = ({
  calculation,
  isSelected,
  onSelect,
}: EvaluationTileProps): JSX.Element => {
  return (
    <div
      className={classNames(style.tile, { [style.selected]: isSelected })}
      onClick={onSelect}
      role="option"
      aria-selected={isSelected}
    >
      <div className={style.header}>
        <span className={style.title}>
          Eval #{calculation.id}
          {calculation.saved && (
            <FloatingTooltip
              placement="right"
              content={
                <>
                  <p className={style.tooltipLabel}>Description:</p>
                  <p className={style.tooltipValue}>
                    {calculation.description || '--'}
                  </p>
                  <p className={style.tooltipLabel}>Notes:</p>
                  <p className={style.tooltipValue}>
                    {calculation.notes || '--'}
                  </p>
                </>
              }
            >
              <span
                className={style.infoIcon}
                onClick={(e) => e.stopPropagation()}
              >
                <Information />
              </span>
            </FloatingTooltip>
          )}
        </span>
        <input
          type="radio"
          className={style.radio}
          checked={isSelected}
          onChange={onSelect}
          aria-label={`Select evaluation ${calculation.id}`}
        />
      </div>
      {calculation.status === 'Error' && (
        <Tag type="red" size="sm">
          Error
        </Tag>
      )}
      {calculation.saved && (
        <Tag type="high-contrast" size="sm">
          Saved
        </Tag>
      )}
      <div className={style.field}>
        <span className={style.label}>Scenario:</span>
        <Link
          to={calculation.scenario.href}
          onClick={(e) => e.stopPropagation()}
        >
          {calculation.scenario.name}
        </Link>
      </div>
      {calculation.eval2 && (
        <div className={style.field}>
          <span className={style.label}>Eval 2 Base:</span>
          <span className={style.value}>#{calculation.eval2.id}</span>
        </div>
      )}
      <div className={style.field}>
        <span className={style.label}>User:</span>
        <span className={style.value}>
          {calculation.runByUser?.name ?? 'Unknown'}
        </span>
      </div>
      <div className={style.field}>
        <span className={style.label}>Date:</span>
        <span className={style.value}>
          {parseLocalDateTime(calculation.createdAt, true)}
        </span>
      </div>
    </div>
  );
};
